import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import StaffTitle from './StaffTitle';
import SearchBox from './SearchBox';
import StaffLists from './StaffLists';
import StaffDetailDialog from './StaffDetailDialog';
import UserPrompt from '../UserPrompt';
import config from '../../config';
import { isServiceVendor } from '../../lib/tools';
import CreateStaffDialog from './CreateStaffDialog';
import { Main, Block, ListsWrapper } from './styles/StaffPageStyles';
import RestoreStaffDialog from './RestoreStaffDialog';
import { ACCOUNT_STATUS, ROLE } from '../../lib/constants';

const {
  searchCompany,
  searchUserName,
  searchUserRole,
  searchAccountStatus,
  searchRemoteSupportStatus,
} = config.customRole.functionAccess;

/**
 * ユーザー管理画面
 *
 * @param {Object} props
 * @param {number} props.searchBoxHeight - 検索ボックスの高さ
 * @returns {ReactElement}
 */
const StaffPage = ({ searchBoxHeight }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;
  const promptMessage = formatMessage({ id: 'userPrompt.message.ownUpdate' });

  // TODO: 必要なコンポーネントで呼び出す
  const {
    userInfo,
    operators,
    companies,
    selectedStatus,
    selectedCompanyId,
    currentCompanyInfo,
    namePartial,
    openUpdateStaffDialog,
    selectedRoleId,
    selectedRemoteSupportStatus,
  } = useSelector(state => ({
    userInfo: state.main.userInfo,
    operators: state.operator.operators,
    remoteMembers: state.operator.remoteMembers,
    companies: state.company.companies,
    selectedStatus: state.company.selectedStatus,
    selectedCompanyId: state.company.selectedCompanyId,
    currentCompanyInfo: state.company.currentCompanyInfo,
    namePartial: state.company.namePartial,
    openUpdateStaffDialog: state.operator.openUpdateStaffDialog,
    selectedRoleId: state.company.selectedRoleId,
    selectedRemoteSupportStatus: state.company.selectedRemoteSupportStatus,
  }));

  const [showCreateStaffDialog, setShowCreateStaffDialog] = useState(false);
  const [showRestoreUserDialog, setShowRestoreUserDialog] = useState(false);

  const handleOpenCreateStaffDialog = () => {
    setShowCreateStaffDialog(true);
  };
  const handleCloseCreateStaffDialog = () => {
    setShowCreateStaffDialog(false);
  };

  const buttonOk = formatMessage({ id: 'userPrompt.button.close' });
  const buttonArray = [{ name: buttonOk, type: 0 }];
  const onAccept = null;
  const displaySearchOption = searchCompany.indexOf(userInfo.role) !== -1;
  const displaySearchUserName = searchUserName.indexOf(userInfo.role) !== -1;
  const displaySearchUserRole = searchUserRole.indexOf(userInfo.role) !== -1;
  const displaySearchAccountStatus =
    searchAccountStatus.indexOf(userInfo.role) !== -1;
  const displaySearchRemoteSupportStatus =
    searchRemoteSupportStatus.indexOf(userInfo.role) !== -1;
  const remoteSupportEnabledSp =
    (currentCompanyInfo &&
      currentCompanyInfo.remoteSupportLicense &&
      currentCompanyInfo.remoteSupportLicense.enabled) ||
    false;
  const remoteSupportEnabled =
    userInfo.companyLicenseInfo.remoteSupportLicenseInfo.enabled;
  const enabledSearchRemoteSupportStatus =
    displaySearchRemoteSupportStatus &&
    (isServiceVendor(userInfo.role) ? true : remoteSupportEnabled);
  const enabledUpdateRemoteSupportStatus = isServiceVendor(userInfo.role)
    ? remoteSupportEnabledSp
    : remoteSupportEnabled;

  const isServiceVenderUser = isServiceVendor(userInfo.role);

  return (
    <Main className="staffPage">
      <Block>
        <StaffTitle
          currentUserRole={userInfo.role}
          onOpenCreateStaffDialog={handleOpenCreateStaffDialog}
        />
        <SearchBox
          role={userInfo.role}
          selectedCompanyId={selectedCompanyId}
          selectedRoleId={selectedRoleId}
          namePartial={namePartial}
          selectedStatus={selectedStatus}
          selectedRemoteSupportStatus={selectedRemoteSupportStatus}
          companies={companies}
          displaySearchUserName={displaySearchUserName}
          displaySearchUserRole={displaySearchUserRole}
          displaySearchAccountStatus={displaySearchAccountStatus}
          displaySearchRemoteSupportStatus={enabledSearchRemoteSupportStatus}
          displaySearchOption={displaySearchOption}
          onSearchUserName={name =>
            dispatch({ type: 'SEARCH_USER_NAME', namePartial: name })
          }
          onSelectCompany={companyId =>
            dispatch({ type: 'SELECT_COMPANY', companyId })
          }
          onSelectRole={roleId => dispatch({ type: 'SELECT_ROLE', roleId })}
          onSelectStatus={status =>
            dispatch({ type: 'SELECT_ACCOUNT_STATUS', status })
          }
          onSelectRemoteSupportStatus={status =>
            dispatch({ type: 'SELECT_REMOTE_SUPPORT_STATUS', status })
          }
        />
        <ListsWrapper searchBoxHeight={searchBoxHeight}>
          <StaffLists
            operators={operators}
            onSelect={(operatorId, companyCode, accountStatus) => {
              const id = operatorId;
              if (userInfo.operatorId !== id) {
                dispatch({ type: 'OPEN_UPDATE_STAFF_DIALOG', id, companyCode });
              } else {
                dispatch({
                  type: 'OPEN_USER_PROMPT',
                  promptMessage,
                  buttonArray,
                  onAccept,
                });
              }
              if (
                accountStatus === ACCOUNT_STATUS.delete &&
                userInfo.role === ROLE.COMPANY_ADMIN
              ) {
                dispatch({ type: 'CLOSE_UPDATE_STAFF_DIALOG' });
                setShowRestoreUserDialog(true);
              }
            }}
            isServiceVenderUser={isServiceVenderUser}
          />
        </ListsWrapper>
        {openUpdateStaffDialog && (
          <StaffDetailDialog
            role={userInfo.role}
            enabledUpdateRemoteSupportStatus={enabledUpdateRemoteSupportStatus}
          />
        )}
        {showRestoreUserDialog && (
          <RestoreStaffDialog
            onCancel={() => setShowRestoreUserDialog(false)}
          />
        )}
        {showCreateStaffDialog && (
          <CreateStaffDialog
            onCloseCreateStaffDialog={handleCloseCreateStaffDialog}
          />
        )}
        <UserPrompt />
      </Block>
    </Main>
  );
};

StaffPage.propTypes = {
  searchBoxHeight: PropTypes.number.isRequired,
};

export default StaffPage;
